/**
 * Listado de Pagos a Incautadores — migración web de ares5090.
 *
 * Consulta de solo lectura de `pagoincautadores`: filtra por chasis O por
 * rango de fechas (excluyentes, como el escritorio — al escribir chasis se
 * deshabilitan las fechas; el rango filtra por fecha de creación) y por
 * sucursal. Exporta a Excel con fila de total y — mejora sobre el
 * escritorio — reimprime el recibo del pago (agrupado por número de recibo).
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, StatusPill, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { puedeAccion } from "lib/seguridad/permisosPorModulo";
import { imprimirRecibo } from "modules/incautos/pagosincautadores";

// Extrae el código numérico de una etiqueta de sucursal "5-LOS MINA" -> "5".
const codigoSucursal = (label) => {
  const s = String(label || "")
    .split("-")[0]
    .trim();
  return s === "0" || s === "" ? "" : s;
};

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin pagos en el rango seleccionado
      </Typography>
    </Stack>
  );
}

export default function ListaPagosIncautadores({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const sucursales = useSucursales({ conTodos: true });
  const [{ roleDetalles }] = useMaterialUIController();
  // Anular un recibo: acción "borrar" del listado de pagos (ares5090).
  const puedeAnular = puedeAccion(roleDetalles, "ares5090", "borrar");
  const [anular, setAnular] = useState(null); // recibo
  const [razon, setRazon] = useState("");
  const [anulando, setAnulando] = useState(false);

  const [chasis, setChasis] = useState("");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [sucursal, setSucursal] = useState("0-TODOS");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/listapagoincautadores", {
        chasis: chasis.trim(),
        fechadesde: fecha1,
        fechahasta: fecha2,
        sucursal: codigoSucursal(sucursal),
      });
      if (ok) {
        // En tablas del escritorio pagoincautadores.id no es autoincremental
        // y llega nulo; el DataGrid exige una clave única por fila.
        setRows((data.pagos || []).map((r, i) => ({ ...r, filakey: i })));
        setTotal(data.total || 0);
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los pagos", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const reimprimir = async (row) => {
    const body = row.recibo ? { recibo: row.recibo } : { id: row.id };
    const { ok, data } = await post("/api/reciboincautador", body);
    if (ok && data.recibo) imprimirRecibo(data.recibo);
    else showAlert({ message: data.Error || "No se pudo recuperar el recibo", color: "error" });
  };

  const confirmarAnular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/pagoincautador_anular", {
        recibo: anular,
        razon: razon.trim(),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Recibo anulado", color: "success" });
        setAnular(null);
        setRazon("");
        cargar();
      } else showAlert({ message: data.Error || "No se pudo anular el recibo", color: "error" });
    } finally {
      setAnulando(false);
    }
  };

  // Export a Excel con fila de total (equivalente al exportarexcel del escritorio).
  const exportarExcel = () => {
    if (!rows.length) {
      showAlert({ message: "No hay datos para exportar", color: "warning" });
      return;
    }
    const encabezados = [
      "# Préstamo",
      "Fecha",
      "Cédula",
      "Nombre incautador",
      "Chasis",
      "Valor",
      "Forma de pago",
      "Banco / referencia",
      "Estado",
      "Descripción",
      "Sucursal",
    ];
    const filas = rows.map((r) => [
      r.noprest,
      r.fecha,
      r.cedula,
      r.nombreincautador,
      r.chasis,
      r.valor,
      r.formapago,
      [r.banco, r.referencia].filter(Boolean).join(" "),
      r.anulado ? `Anulado: ${r.razonanula}` : "",
      r.descripcion,
      r.sucursal,
    ]);
    const hoja = XLSX.utils.aoa_to_sheet([
      encabezados,
      ...filas,
      [],
      ["", "", "", "", "TOTAL:", total],
    ]);
    hoja["!cols"] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 30 },
      { wch: 22 },
      { wch: 14 },
      { wch: 14 },
      { wch: 24 },
      { wch: 30 },
      { wch: 40 },
      { wch: 12 },
    ];
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Pagos a Incautadores");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
    XLSX.writeFile(libro, `ListadoPagosIncautadores_${stamp}.xlsx`);
  };

  const columns = [
    { field: "recibo", headerName: "Recibo", width: 80 },
    { field: "noprest", headerName: "# Préstamo", width: 100 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "cedula", headerName: "Cédula", width: 135 },
    { field: "nombreincautador", headerName: "Nombre incautador", flex: 1, minWidth: 170 },
    { field: "chasis", headerName: "Chasis", width: 175 },
    {
      field: "valor",
      headerName: "Valor",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "formapago",
      headerName: "Forma de pago",
      width: 150,
      renderCell: ({ row }) =>
        row.anulado ? (
          <Tooltip title={row.razonanula || ""}>
            <span>
              <StatusPill label="Anulado" variant="danger" />
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={[row.banco, row.referencia].filter(Boolean).join(" · ")}>
            <span>{row.formapago}</span>
          </Tooltip>
        ),
    },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 150 },
    { field: "sucursal", headerName: "Sucursal", width: 95 },
    {
      field: "acciones",
      headerName: "",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ height: "100%" }}>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => reimprimir(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Recibo
          </Button>
          {puedeAnular && params.row.recibo && !params.row.anulado ? (
            <Tooltip title={`Anular el recibo ${params.row.recibo} completo`}>
              <Button
                size="small"
                onClick={() => {
                  setRazon("");
                  setAnular(params.row.recibo);
                }}
                sx={btnOutline(FP.color.danger)}
              >
                <BlockOutlinedIcon fontSize="small" />
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Listado de Pagos a Incautadores"
          subtitle="Consulta los pagos realizados a incautadores, reimprime recibos y exporta a Excel"
          cta={
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportarExcel}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Exportar Excel
            </Button>
          }
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        {embedded && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportarExcel}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Exportar Excel
            </Button>
          </Stack>
        )}
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Chasis"
                placeholder="Dejar vacío para buscar por fechas"
                value={chasis}
                onChange={(e) => setChasis(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                disabled={!!chasis.trim()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                disabled={!!chasis.trim()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                {sucursales.map((s) => (
                  <MenuItem key={s.label} value={s.label}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={12} md={3}>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
                <Button
                  startIcon={
                    loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                  }
                  onClick={cargar}
                  disabled={loading}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Procesar
                </Button>
              </Stack>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }} justifyContent="flex-end">
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
              {rows.length} registros · Total (sin anulados): RD$ {fmtMoney(total)}
            </Typography>
          </Stack>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              getRowId={(r) => r.filakey}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
      <Dialog open={anular !== null} onClose={() => !anulando && setAnular(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Anular el recibo {anular}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 1 }}>
            Se anulan todas las líneas del recibo y sus incautos vuelven a quedar pendientes de
            pago. Si fue en efectivo, el monto deja de restarse en el cuadre.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Razón"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            inputProps={{ maxLength: 250 }}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAnular(null)} disabled={anulando} sx={btnBase}>
            Volver
          </Button>
          <Button
            onClick={confirmarAnular}
            disabled={anulando || !razon.trim()}
            sx={btnSolid(FP.color.danger, "#BE123C")}
          >
            Anular recibo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
